/**
 * AGUIProvider - Context provider for AG-UI client and global state
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AGUIClient } from '../services/aguiClient';
import { BaseEvent } from '../types/agui';

interface AGUIContextValue {
  client: AGUIClient;
  isConnected: boolean;
  error: Error | null;
  events: BaseEvent[]; // Use AG-UI BaseEvent type from library
}

const AGUIContext = createContext<AGUIContextValue | null>(null);

export function AGUIProvider({ children }: { children: ReactNode }) {
  const [client] = useState(() => new AGUIClient());
  const [isConnected, setIsConnected] = useState(false);
  const [error] = useState<Error | null>(null);
  const [events] = useState<BaseEvent[]>([]); // Use AG-UI BaseEvent type

  useEffect(() => {
    // Initialize token and ensure it's set on the client
    const token =
      localStorage.getItem('flowrithm_jwt_token') ||
      sessionStorage.getItem('flowrithm_jwt_token');
    
    if (token) {
      client.setToken(token);
      setIsConnected(true);
    } else {
      setIsConnected(false);
    }

    // Listen for storage changes (e.g., when user logs in/out)
    const handleStorageChange = () => {
      const newToken =
        localStorage.getItem('flowrithm_jwt_token') ||
        sessionStorage.getItem('flowrithm_jwt_token');
      
      if (newToken !== token) {
        client.setToken(newToken);
        setIsConnected(!!newToken);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [client]);

  const value: AGUIContextValue = {
    client,
    isConnected,
    error,
    events,
  };

  return <AGUIContext.Provider value={value}>{children}</AGUIContext.Provider>;
}

export function useAGUIContext(): AGUIContextValue {
  const context = useContext(AGUIContext);
  if (!context) {
    throw new Error('useAGUIContext must be used within AGUIProvider');
  }
  return context;
}
